import { useState } from 'react'
import dayjs from 'dayjs'
import { ThumbsDown, ThumbsUp } from 'react-feather'
import { Button, Card, Modal, Text, TextField } from '@/components'
import { useAppDispatch } from '@/hooks'
import { surveyResponsesCollection } from '@/reducers/surveyResponses'
import { useUserSurveyOf, SurveyNames } from '@/app/hooks/useUserSurveyOf'

export type FeatureFeedbackRating = 'up' | 'down'

type ViewProps = {
  question: string
  onSubmit: (rating: FeatureFeedbackRating, feedback: string) => void
}

/**
 * Presentational half, exported for Storybook: the inline banner with
 * thumbs up / down, and the "Tell us more" modal a thumb opens.
 */
export function FeatureFeedbackBannerView({ question, onSubmit }: ViewProps) {
  const [rating, setRating] = useState<FeatureFeedbackRating | null>(null)
  const [feedback, setFeedback] = useState('')

  // No global preflight: strip the UA button chrome explicitly
  const thumbClass =
    'bg-transparent border-0 p-0 cursor-pointer text-themed-grey-700 flex items-center'

  return (
    <>
      <Card fullWidth className="box-border">
        <div className="flex items-center gap-4 h-auto">
          <Text variant="body2" className="flex-1">
            {question}
          </Text>
          <button
            aria-label="Thumbs up"
            className={thumbClass}
            onClick={() => setRating('up')}
          >
            <ThumbsUp size={20} />
          </button>
          <button
            aria-label="Thumbs down"
            className={thumbClass}
            onClick={() => setRating('down')}
          >
            <ThumbsDown size={20} />
          </button>
        </div>
      </Card>
      {rating !== null && (
        <Modal
          id="feature-feedback-modal"
          open
          onClose={() => setRating(null)}
          title="Thanks for the feedback!"
        >
          <div className="flex flex-col gap-4">
            <TextField
              id="feature-feedback-text"
              label="Tell us more"
              onChange={(event) => setFeedback(event.target.value)}
            />
            <Button
              id="feature-feedback-submit"
              variant="primary"
              onClick={() => onSubmit(rating, feedback)}
            >
              Submit
            </Button>
          </div>
        </Modal>
      )}
    </>
  )
}

type Props = {
  /** The Survey row's name — one FeatureFeedback survey per feature */
  surveyName: SurveyNames
  question: string
}

/**
 * Inline feature-feedback banner backed by the platform survey framework.
 * Renders only while the user's FeatureFeedback SurveyResponse is pending;
 * submitting (or having previously completed it) hides it. Reusable for any
 * feature rollout: create a Survey row with type "FeatureFeedback", add its
 * name to SurveyNames, and mount this with that name.
 */
export default function FeatureFeedbackBanner({ surveyName, question }: Props) {
  const dispatch = useAppDispatch()
  const response = useUserSurveyOf(surveyName)

  if (
    !response ||
    response.survey.type !== 'FeatureFeedback' ||
    !(response.pending_response || response.manual_show_flag)
  ) {
    return null
  }

  const onSubmit = (rating: FeatureFeedbackRating, feedback: string) => {
    // Target this exact response row (not the by-type responder hook) so
    // several FeatureFeedback surveys can coexist on one page
    dispatch(
      surveyResponsesCollection.actions.update(
        response.id,
        {
          last_seen: dayjs().toISOString(),
          completed: dayjs().toISOString(),
          response: {
            rating,
            feedback: feedback || null,
            version: response.survey.version,
          },
        },
        { optimistic: true },
      ),
    )
  }

  return <FeatureFeedbackBannerView question={question} onSubmit={onSubmit} />
}
